import React, { useEffect, useRef, useState } from "react";
import { sankey, sankeyLinkHorizontal, sankeyLeft } from "d3-sankey";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { X } from "lucide-react";

/**
 * EpigeneticMapVisualization — D3 Sankey diagram.
 * 3 columns: INPUTS (lifestyle/family/dx/labs) → PATHWAYS → OUTCOMES (hidden risks).
 *
 * Color coding:
 *  - current_disease: rose
 *  - family_history: amber
 *  - lifestyle: violet
 *  - lab_gap: cyan
 *  - pathway: emerald
 *  - hidden_risk: pink
 */

const KIND_COLORS = {
  current_disease: "#f43f5e", // rose-500
  family_history: "#f59e0b", // amber-500
  lifestyle: "#8b5cf6", // violet-500
  lab_gap: "#06b6d4", // cyan-500
  pathway: "#10b981", // emerald-500
  hidden_risk: "#ec4899", // pink-500
};

export default function EpigeneticMapVisualization({ data, onPathwayClick }) {
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const svgRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    if (!data || !data.nodes || data.nodes.length === 0) return;
    const svg = svgRef.current;
    if (!svg) return;

    // Clear
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    const width = svg.clientWidth || 800;
    const height = Math.max(440, data.nodes.length * 14);
    svg.setAttribute("height", String(height));

    const sankeyGen = sankey()
      .nodeWidth(14)
      .nodePadding(8)
      .nodeAlign(sankeyLeft)
      .extent([
        [16, 12],
        [width - 16, height - 12],
      ]);

    // Clone since sankey mutates
    const nodesCopy = data.nodes.map((n) => ({ ...n }));
    const linksCopy = data.links.map((l) => ({ ...l }));

    const { nodes, links } = sankeyGen({ nodes: nodesCopy, links: linksCopy });

    // SVG primitives
    const NS = "http://www.w3.org/2000/svg";

    // Defs for link gradients
    const defs = document.createElementNS(NS, "defs");
    svg.appendChild(defs);

    // Links
    links.forEach((link, i) => {
      const path = document.createElementNS(NS, "path");
      path.setAttribute("d", sankeyLinkHorizontal()(link));
      const sourceColor = KIND_COLORS[link.source.kind] || "#6b7280";
      const targetColor = KIND_COLORS[link.target.kind] || "#6b7280";

      const gradId = `epimap-grad-${i}`;
      const grad = document.createElementNS(NS, "linearGradient");
      grad.setAttribute("id", gradId);
      grad.setAttribute("gradientUnits", "userSpaceOnUse");
      grad.setAttribute("x1", String(link.source.x1));
      grad.setAttribute("x2", String(link.target.x0));
      const stop1 = document.createElementNS(NS, "stop");
      stop1.setAttribute("offset", "0%");
      stop1.setAttribute("stop-color", sourceColor);
      stop1.setAttribute("stop-opacity", "0.6");
      const stop2 = document.createElementNS(NS, "stop");
      stop2.setAttribute("offset", "100%");
      stop2.setAttribute("stop-color", targetColor);
      stop2.setAttribute("stop-opacity", "0.6");
      grad.appendChild(stop1);
      grad.appendChild(stop2);
      defs.appendChild(grad);

      path.setAttribute("fill", "none");
      path.setAttribute("stroke", `url(#${gradId})`);
      path.setAttribute("stroke-width", String(Math.max(1, link.width)));
      path.setAttribute("opacity", "0.7");
      svg.appendChild(path);
    });

    // Nodes
    nodes.forEach((node) => {
      const g = document.createElementNS(NS, "g");
      g.setAttribute("data-testid", `epimap-node-${node.id}`);
      g.style.cursor = "pointer";
      g.addEventListener("click", () => {
        setSelectedNode(node);
        if (node.kind === "pathway" && onPathwayClick) {
          // Extract original KEGG/Reactome ID from "path:hsa04930"
          const pid = String(node.id).startsWith("path:") ? String(node.id).slice(5) : node.id;
          onPathwayClick(pid);
        }
      });

      const rect = document.createElementNS(NS, "rect");
      rect.setAttribute("x", String(node.x0));
      rect.setAttribute("y", String(node.y0));
      rect.setAttribute("width", String(node.x1 - node.x0));
      rect.setAttribute("height", String(Math.max(2, node.y1 - node.y0)));
      rect.setAttribute("fill", KIND_COLORS[node.kind] || "#6b7280");
      rect.setAttribute("rx", "2");
      g.appendChild(rect);

      const txt = document.createElementNS(NS, "text");
      const onLeft = node.x0 < width / 2;
      txt.setAttribute("x", String(onLeft ? node.x1 + 4 : node.x0 - 4));
      txt.setAttribute("y", String((node.y0 + node.y1) / 2));
      txt.setAttribute("dy", "0.35em");
      txt.setAttribute("text-anchor", onLeft ? "start" : "end");
      txt.setAttribute("fill", isDark ? "#e5e7eb" : "#111827");
      txt.setAttribute("font-size", "10");
      txt.setAttribute("font-family", "system-ui, sans-serif");
      txt.textContent = node.label.length > 28 ? node.label.slice(0, 26) + "…" : node.label;
      g.appendChild(txt);

      svg.appendChild(g);
    });
  }, [data, isDark]);

  if (!data || !data.nodes || data.nodes.length === 0) {
    return (
      <div
        className={`rounded-2xl border p-6 text-center ${
          isDark ? "bg-[hsl(223,47%,10%)] border-white/5 text-gray-500" : "bg-white border-gray-200 text-gray-500"
        }`}
      >
        {bg ? "Няма данни за визуализация." : "No data to visualize."}
      </div>
    );
  }

  // Legend
  const LEGEND_BG = [
    { kind: "current_disease", label: "Текуща диагноза" },
    { kind: "family_history", label: "Фамилна анамнеза" },
    { kind: "lifestyle", label: "Lifestyle" },
    { kind: "lab_gap", label: "Лабораторна аномалия" },
    { kind: "pathway", label: "Молекулярен път" },
    { kind: "hidden_risk", label: "Скрит риск" },
  ];
  const LEGEND_EN = [
    { kind: "current_disease", label: "Current dx" },
    { kind: "family_history", label: "Family history" },
    { kind: "lifestyle", label: "Lifestyle" },
    { kind: "lab_gap", label: "Lab anomaly" },
    { kind: "pathway", label: "Pathway" },
    { kind: "hidden_risk", label: "Hidden risk" },
  ];
  const legend = bg ? LEGEND_BG : LEGEND_EN;

  return (
    <div
      className={`rounded-2xl border p-3 ${
        isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"
      }`}
      data-testid="epigenetic-map-visualization"
    >
      <div className="flex flex-wrap items-center gap-2 mb-2 px-1">
        {legend.map((l) => (
          <span key={l.kind} className="inline-flex items-center gap-1 text-[10px]">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: KIND_COLORS[l.kind] }} />
            {l.label}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto">
        <svg ref={svgRef} width="100%" style={{ minWidth: 700 }} />
      </div>
      <p className={`text-[10px] mt-2 px-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
        {bg
          ? "Дебелината на потока показва интензивността на връзката между входни данни → молекулярни пътища → потенциални рискове."
          : "Flow thickness reflects intensity of input → pathway → outcome relationships."}
      </p>
    </div>
  );
}
